import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../config/firebaseConfig";
import { Button } from "../../components/catalyst/button";
import MemberDetailsCard from "../../components/MemberDetailsCard";
import { Shimmer } from "../../components/ShimmerComponent";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";

import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { ChevronLeftIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/20/solid";

import { useToast } from "../../util/ToastProvider";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

export default function AccessReportPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<any>(null);
  const [reportUserId, setReportUserId] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // accordions
  const [openOpening, setOpenOpening] = useState(false);
  const [openClosing, setOpenClosing] = useState(false);

  const [consentLoading, setConsentLoading] = useState<Record<number, boolean>>({});

  // mobile account dropdown
  const [showMobileAccount, setShowMobileAccount] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);

  // annexure visibility states: true=show, false=show consent, "hidden"=completely hidden
  type AnnexureState = boolean | "hidden";
  const [annexureVisible, setAnnexureVisible] = useState<AnnexureState[]>([]);
  const performReportReviewTask = httpsCallable(
    functions,
    "performReportReview"
  );
  const useToaster = useToast()

  // fetch
  useEffect(() => {
    if (!reportId) return;

    const fetchUserReport = async () => {
      try {
        setLoading(true);
        const callFn = httpsCallable(functions, "manageProfile");

        const res: any = await callFn({
          action: "getUserReport",
          data: { reportId },
        });
        incrementReportView(reportId)


        
        setReport(res.data?.data || null);
        setReportUserId(res.data?.data.user.userId)
        setError(null);
      } catch (err: any) {
        setError("Failed to fetch report.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserReport();
  }, [reportId]);

  // init annexureVisible from report
  useEffect(() => {
    if (report?.annexure) {
      setAnnexureVisible(
        report.annexure.map((a: any) => {
          if (a.consentRequired && a.consentGiven === true) {
            return true; // already allowed → show directly
          }
          return !a.consentRequired;
        })
      );
    }
  }, [report]);


  // close mobile menu if clicked outside
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setShowMobileAccount(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ---------------------------
  // Download Handler
  // ---------------------------
  const handleDownload = async () => {
    // const combined = `
    // ${report?.finalData?.openingText || ""}

    // ${report?.finalData?.text || ""}

    // ${report?.finalData?.closingText || ""}
    // `.trim(); 

    // const blob = new Blob([combined], { type: "text/plain" });
    // const url = URL.createObjectURL(blob);

    // const a = document.createElement("a");
    // a.href = url;
    // a.download = `report-${reportId}.txt`;
    // a.click();

    // URL.revokeObjectURL(url);

    if (!report) return;

    // Opening + main + closing
    let combined = `
    ${report?.finalData?.openingText || ""}
    
    ${report?.finalData?.text || ""}
    
    ${report?.finalData?.closingText || ""}
    `.trim();

    // Append annexures ONLY if allowed
    if (Array.isArray(report.annexure)) {
      report.annexure.forEach((item: any, index: number) => {
        const visible = annexureVisible[index];

        // include only if explicitly visible
        if (visible === true && item?.output) {
          combined += `
    
    --- Annexure: ${item.title || "Untitled"} ---
    
    ${item.output}
    `;
        }
      });
    }

    await navigator.clipboard.writeText(combined);
    useToaster.success("Report copied to clipboard");



  };

  const giveAnnexureConsent = async (annexureId: string, index: number) => {
    if (!reportId) return;

    try {
      // disable buttons for this annexure
      setConsentLoading((prev) => ({ ...prev, [index]: true }));

      await performReportReviewTask({
        action: "updateAnnexure",
        reportId,
        annexureId,
      });

      // ✅ success → show annexure
      setAnnexureVisible((prev) => {
        const copy = [...prev];
        copy[index] = true;
        return copy;
      });

      useToaster.success("Consent recorded. Annexure unlocked.");
    } catch (err: any) {
      console.error(err);
      useToaster.error(
        err?.message || "Failed to record consent. Please try again."
      );
    } finally {
      setConsentLoading((prev) => ({ ...prev, [index]: false }));
    }
  };


  async function incrementReportView(reportId: string) {
    try {
      const res: any = await performReportReviewTask({
        action: "ViewReportCount",
        reportId,
      });

      console.log("View count updated:", res.data.viewCount);
      return res.data.viewCount;
    } catch (err: any) {
      console.error("Failed to increment view count:", err.message);
      throw err;
    }
  }

  function backToHome(){
    if(user?.role === "admin"){
      navigate(`/admin/user-view/${reportUserId}`)
    }else{
      navigate("/")
    }
  }

  return (
    <div className="min-h-screen bg-dark-background text-white">
      {/* DESKTOP HEADER (hidden on small screens) */}
      <header className="hidden sm:block sticky top-0 z-40 border-b border-dark-border backdrop-blur bg-transparent">
        <div className="mx-auto px-6 h-16 flex items-center justify-between">
          <img src="/assets/QID-logo.svg" alt="QID logo" className="h-8" />
        </div>
      </header>

      {/* MOBILE HEADER */}
      <header className="sm:hidden sticky top-0 z-40 border-b border-dark-border bg-dark-background backdrop-blur">
        <div className="mx-auto px-4 h-16 flex items-center justify-between">
          <img src="/assets/QID-logo.svg" alt="QID logo" className="h-7" />


        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="rounded-2xl pb-6 bg-dark-background-secondary p-6">
          {/* LOADING */}
          {loading && <div className="text-xl px-4 py-3">
            <div className="flex flex-col sm:justify-between sm:flex-row gap-4">
              <Shimmer className="h-[800px] w-full sm:w-[880px] rounded-2xl" />

              <Shimmer className="h-[400px] w-full sm:w-[280px] rounded-2xl" />
            </div>
          </div>}

          {/* ERROR */}
          {error && <h2 className="text-red-400 text-xl px-4 py-3">{error}</h2>}

          {/* SUCCESS */}
          {!loading && !error && report && (
            <>
              {/* ===== MOBILE TOP BLOCK ===== */}
              <div className="sm:hidden mb-6">
                {/* Back */}
                <button
                  onClick={backToHome}
                  className="flex items-center gap-2 text-dark-text font-medium text-[16px]"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                  Back to home
                </button>

                {/* Title */}
                <h2 className="text-xl font-semibold mt-4">
                  {report.title || "Your Report"}
                </h2>

                <div className="mb-4 items-center ">
                  {report.status === "outdated" ? (
                    <span className="bg-red-600 items-center text-center text-white text-xs font-semibold px-2 py-1 rounded">
                      Outdated
                    </span>
                  ) : ''}
                </div>

                {/* Download button (second line on mobile) */}
                <Button
                  type="button"
                  color="brand"
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 mt-4 "
                >
                  <ClipboardDocumentCheckIcon className="w-4 h-4 text-white" />
                  Copy
                </Button>

                {/* Account details (alternate mobile place) */}
                <div className="mt-4 border border-dark-border rounded-lg">
                  <button
                    onClick={() => setShowMobileAccount((v) => !v)}
                    className="w-full flex justify-between px-4 py-3 font-medium"
                  >
                    <span>Account details</span>
                    <ChevronDownIcon className={`w-5 h-5 transition-transform ${showMobileAccount ? "rotate-180" : ""}`} />
                  </button>
                  {/* when expanded, show full MemberDetailsCard inline (not absolute) */}
                  {showMobileAccount && (
                    <div className="p-4">
                      <MemberDetailsCard data={report.user} dark={true} showMath={false} />
                    </div>
                  )}
                </div>

                {/* How to Read This Report (mobile full-width) */}
                <div className="mt-4 border border-dark-border rounded-lg">
                  <button
                    onClick={() => setOpenOpening(!openOpening)}
                    className="w-full flex justify-between px-4 py-3 font-medium"
                  >
                    <span>How to Read This Report</span>
                    <ChevronDownIcon className={`w-5 h-5 transition-transform ${openOpening ? "rotate-180" : ""}`} />
                  </button>

                  {openOpening && (
                    <div className="px-4 pb-4 prose prose-invert">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw, rehypeSanitize]}
                      >
                        {report?.finalData?.openingText || ""}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>

              {/* ===== DESKTOP/TABLET TOP BLOCK (hidden on mobile) ===== */}
              <div className="hidden sm:block">
                {/* Breadcrumb */}
                <button
                  onClick={backToHome}
                  className="flex items-center gap-2 text-dark-text mb-6 font-medium text-[16px]"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                  Back to home
                </button>

                {/* Title + Download button */}
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-semibold">
                    {report.title || "Your Report"}
                  </h2>



                  <Button
                    type="button"
                    color="brand"
                    onClick={handleDownload}
                    className="flex items-center gap-2 px-4 py-2"
                  >
                    <ClipboardDocumentCheckIcon className="w-4 h-4 text-white" />
                    Copy
                  </Button>
                </div>
              </div>
              <div className="mb-4 items-center hidden sm:block">
                {report.status === "outdated" ? (
                  <span className="bg-red-600 items-center text-center text-white text-xs font-semibold px-2 py-1 rounded">
                    Outdated
                  </span>
                ) : ''}
              </div>

              {/* MAIN GRID */}
              <div className="grid grid-cols-1 sm:grid-cols-[70%_5%_25%] gap-0">
                {/* LEFT SIDE */}
                <div>
                  {/* (On desktop, opening accordion already rendered above for mobile; keep a desktop version too) */}
                  {/** Desktop opening accordion (hidden on mobile to avoid duplication) */}
                  <div className="hidden sm:block border border-dark-border rounded-lg mb-4">
                    <button
                      onClick={() => setOpenOpening(!openOpening)}
                      className="w-full flex justify-between px-4 py-3 font-medium"
                    >
                      <span>How to Read This Report</span>
                      <ChevronDownIcon
                        className={`w-5 h-5 transition-transform ${openOpening ? "rotate-180" : ""}`}
                      />
                    </button>

                    {openOpening && (
                      <div className="px-4 pb-4 prose prose-invert">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw, rehypeSanitize]}
                        >
                          {report?.finalData?.openingText || ""}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>

                  {/* MAIN REPORT TEXT */}
                  <div className="prose prose-invert max-w-none mb-6">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw, rehypeSanitize]}
                    >
                      {report?.finalData?.text || ""}
                    </ReactMarkdown>
                  </div>

                  {/* ANNEXURE SECTION */}
                  {report?.annexure && Array.isArray(report.annexure) && report.annexure.length > 0 && (
                    <div className="space-y-6 mb-6">
                      {report.annexure.map((item: any, index: number) => {
                        const visible = annexureVisible[index];

                        // If user clicked "No", hide entire block
                        if (visible === "hidden") return null;

                        return (
                          <div key={index} className="border border-dark-border rounded-xl p-4 bg-black/20">
                            {item.consentRequired && !visible ? (
                              // CONSENT BOX
                              <div className="space-y-3">
                                <div className="font-semibold flex items-center gap-2">
                                  <span className="text-yellow-400">⚠</span> Consent required
                                </div>

                                <p className="text-sm text-zinc-300">
                                  This report contains an addendum titled{" "}
                                  <strong>&ldquo;{item.title || "Untitled Addendum"}&rdquo;</strong>,
                                  which may contain sensitive content. Would you like to view it?
                                </p>

                                <div className="flex gap-3 mt-3">
                                  <button
                                    disabled={consentLoading[index]}
                                    onClick={() => {
                                      setAnnexureVisible((prev) => {
                                        const copy = [...prev];
                                        copy[index] = "hidden";
                                        return copy;
                                      });
                                    }}
                                    className={`
    px-3 py-1.5 rounded-lg text-sm
    ${consentLoading[index]
                                        ? "bg-zinc-700/50 cursor-not-allowed"
                                        : "bg-zinc-700 hover:bg-zinc-600"}
  `}
                                  >
                                    No, do not show
                                  </button>


                                  <button
                                    disabled={consentLoading[index]}
                                    onClick={() => giveAnnexureConsent(item.id, index)}
                                    className={`
    px-3 py-1.5 rounded-lg text-sm
    ${consentLoading[index]
                                        ? "bg-brand-600/50 cursor-not-allowed"
                                        : "bg-brand-600 hover:bg-brand-700"}
  `}
                                  >
                                    {consentLoading[index] ? "Unlocking…" : "Yes, show it"}
                                  </button>

                                </div>
                              </div>
                            ) : (
                              // SHOWN RESULT
                              <div className="space-y-2">
                                <div className="font-semibold text-lg">
                                  {item.title || "Annexure"}
                                </div>

                                <div className="prose prose-invert max-w-none">
                                  <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw, rehypeSanitize]}
                                  >
                                    {item.output || ""}
                                  </ReactMarkdown>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Closing Accordion */}
                  <div className="border border-dark-border rounded-lg">
                    <button
                      onClick={() => setOpenClosing(!openClosing)}
                      className="w-full flex justify-between px-4 py-3 font-medium"
                    >
                      <span>Closing Notes</span>
                      <ChevronDownIcon
                        className={`w-5 h-5 transition-transform ${openClosing ? "rotate-180" : ""}`}
                      />
                    </button>

                    {openClosing && (
                      <div className="px-4 pb-4 prose prose-invert">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw, rehypeSanitize]}
                        >
                          {report?.finalData?.closingText || ""}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                </div>

                {/* SPACER */}
                <div></div>

                {/* RIGHT PANEL */}
                <div className="hidden sm:block">
                  <MemberDetailsCard data={report.user} dark={true} />
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
